import React, { useEffect, useState } from 'react';
import { liveQuery } from 'dexie';
import { db } from '../services/db';
import { SyncService } from '../services/syncService';
import { resolveWorkspaceConflict } from '../services/workspaceSyncEngine';
import { useAuth } from '../services/authContext';
import { MutationOperation } from '../types';

// Routine saving, reconnection, and retries are background work. Only render
// when an existing edit requires an explicit user decision or authentication.
export default function WorkspaceSyncStatus() {
  const { user, idToken, signIn } = useAuth();
  const database = db;
  const [operations, setOperations] = useState<MutationOperation[]>([]);
  const [online, setOnline] = useState(navigator.onLine);
  const [review, setReview] = useState(false);
  const [actionError, setActionError] = useState('');
  useEffect(() => {
    const subscription = liveQuery(() => database.outbox.orderBy('created_at_local').toArray()).subscribe(setOperations);
    const connectivity = () => setOnline(navigator.onLine);
    window.addEventListener('online', connectivity);
    window.addEventListener('offline', connectivity);
    return () => {
      subscription.unsubscribe();
      window.removeEventListener('online', connectivity);
      window.removeEventListener('offline', connectivity);
    };
  }, [database, user?.email]);
  const connectionError = (message?: string) => !!message && /fetch|network|abort|connection/i.test(message);
  const conflicts = operations.filter(operation => operation.status === 'conflict');
  const failed = operations.filter(operation => operation.status === 'failed' && !connectionError(operation.error));
  const needsSignIn = online && !!user && !idToken;
  if (!needsSignIn && !conflicts.length && !(online && failed.length)) return null;

  const resolve = async (operation: MutationOperation, choice: 'local' | 'server') => {
    if (database !== db) return;
    setActionError('');
    try {
      await resolveWorkspaceConflict(database, operation.op_id, choice);
      void SyncService.requestSync('mutation');
    } catch { setActionError('Unable to apply that choice. Your work is still saved on this device.'); }
  };
  const name = (operation: MutationOperation) => operation.payload?.label || operation.payload?.name || 'This edit';
  return <div className="flex-shrink-0 border-b border-amber-200 bg-amber-50 text-amber-950 px-4 py-3 text-sm" role="status" aria-live="polite">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p>{needsSignIn ? 'Sign in again to see the latest shared places and keep your work available on other devices.' : conflicts.length ? 'An edit was also changed on another device. Choose which changes to keep.' : 'Some changes could not be saved to your account. Your work is safe on this device.'}</p>
      <div className="flex items-center gap-3">
        {needsSignIn && <button className="font-semibold underline" onClick={() => signIn()}>Sign in</button>}
        {!needsSignIn && failed.length > 0 && <button className="font-semibold underline" onClick={() => void SyncService.requestSync('mutation')}>Try saving again</button>}
        {(conflicts.length > 0 || failed.length > 0) && <button className="font-semibold underline" aria-expanded={review} onClick={() => setReview(!review)}>{review ? 'Hide changes' : 'Review changes'}</button>}
      </div>
    </div>
    {actionError && <p className="mt-2">{actionError}</p>}
    {review && <div className="mt-3 space-y-3">
      {failed.map(operation => <p key={operation.op_id}>{name(operation)} could not be saved to your account. It remains saved on this device.</p>)}
      {conflicts.map(operation => <div className="rounded-lg border border-amber-200 bg-white p-3 space-y-2" key={operation.op_id}>
        <p className="font-semibold">{name(operation)}</p>
        <p>Your changes: {operation.type === 'delete' ? 'Archived' : operation.payload?.label || operation.payload?.name || 'Edited'}{operation.payload?.notes && <span className="block text-gray-600 whitespace-pre-wrap">{operation.payload.notes}</span>}</p>
        <p>Other device: {operation.server_entity?.deleted_at ? 'Archived' : operation.server_entity?.label || operation.server_entity?.name || 'Edited'}{operation.server_entity?.notes && <span className="block text-gray-600 whitespace-pre-wrap">{operation.server_entity.notes}</span>}</p>
        <div className="flex flex-wrap gap-4 pt-1"><button className="underline font-medium" disabled={!operation.server_entity} onClick={() => void resolve(operation, 'local')}>Keep my changes</button><button className="underline font-medium" disabled={!operation.server_entity} onClick={() => void resolve(operation, 'server')}>Keep the other changes</button></div>
      </div>)}
    </div>}
  </div>;
}
